import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useFixturesFromQuery } from '../fixtures'
import { useAppPath } from '../nav'
import type { FinanceTx, TradeHistoryItem } from '../types'
import { api } from '../api'
import {
  financeStatusLabel,
  financeStatusTone,
  financeTypeLabel,
  formatWhen,
  moneyAmount,
  safeError,
} from '../safe-ui'
import { useWorkspaceAccount } from '../WorkspaceAccountProvider'
import { LiveCreateModal } from '../components/LiveCreateModal'
import { QuickActions } from '../components/QuickActions'
import { Button } from '../../components/ui/Button'
import { accountModeBadge } from '../account-badges'

function tradeResultLabel(t: TradeHistoryItem): string {
  const raw = String(t.result || '').toUpperCase()
  if (raw === 'WON') return 'Won'
  if (raw === 'LOST') return 'Lost'
  if (raw === 'BREAKEVEN') return 'Break-even'
  const closed = Boolean(t.close_time)
  const pnl = Number(t.net_profit ?? t.profit)
  if (!closed) return 'Open'
  if (!Number.isFinite(pnl)) return 'Closed'
  if (pnl > 0) return 'Won'
  if (pnl < 0) return 'Lost'
  return 'Break-even'
}

function isFailedFinance(status?: string | null): boolean {
  const s = String(status || '').toUpperCase()
  return s === 'FAILED' || s === 'REJECTED' || s === 'CANCELLED' || s === 'ERROR'
}

export function DashboardPage() {
  const appPath = useAppPath()
  const fixtures = useFixturesFromQuery()
  const ws = useWorkspaceAccount()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [trades, setTrades] = useState<TradeHistoryItem[]>([])
  const [txs, setTxs] = useState<FinanceTx[]>([])
  const [liveModalOpen, setLiveModalOpen] = useState(false)
  const [liveAttemptFailed, setLiveAttemptFailed] = useState(false)

  const liveAccount = ws.accounts.find((a) => {
    const m = String(a.mode || '').toUpperCase()
    return m === 'LIVE' || m === 'REAL'
  })
  const demoAccount = ws.accounts.find((a) => String(a.mode || '').toUpperCase() === 'DEMO')
  const liveBalance =
    liveAccount && ws.account?.id === liveAccount.id
      ? (ws.balance ?? liveAccount.balance)
      : liveAccount?.balance
  const liveEquity =
    liveAccount && ws.account?.id === liveAccount.id
      ? (ws.equity ?? liveAccount.equity)
      : liveAccount?.equity
  const liveAvailable =
    liveAccount && ws.account?.id === liveAccount.id && ws.summary?.free_margin != null
      ? ws.summary.free_margin
      : liveBalance

  const liveAccountId = liveAccount?.id ?? null

  const loadActivity = useCallback(async () => {
    setError('')
    if (fixtures) {
      setTrades([])
      setTxs([])
      return
    }
    if (liveAccountId == null) {
      setTrades([])
      setTxs([])
      return
    }
    const [history, finance] = await Promise.all([
      api.tradeHistory(liveAccountId).catch(() => ({
        trades: [] as TradeHistoryItem[],
        deals: [] as TradeHistoryItem[],
      })),
      api.transactions().catch(() => ({ transactions: [] as FinanceTx[] })),
    ])
    setTrades((history.trades || history.deals || []).slice(0, 6))
    setTxs(
      (finance.transactions || [])
        .filter((tx) => !isFailedFinance(tx.status))
        .filter((tx) => {
          const s = String(tx.status || '').toUpperCase()
          // Keep the dashboard focused on funded / in-flight money, not abandoned invoices.
          return s !== 'EXPIRED' && s !== 'INCOMPLETE_PAYMENT'
        })
        .sort((a, b) => {
          const rank = (status?: string | null) => {
            const s = String(status || '').toUpperCase()
            if (s.includes('COMPLETE') && !s.includes('INCOMPLETE')) return 0
            if (s.includes('CONFIRM') || s.includes('PROCESS')) return 1
            if (s.includes('AWAIT') || s.includes('PEND')) return 2
            return 3
          }
          const byStatus = rank(a.status) - rank(b.status)
          if (byStatus !== 0) return byStatus
          return String(b.created_at || '').localeCompare(String(a.created_at || ''))
        })
        .slice(0, 3),
    )
  }, [fixtures, liveAccountId])

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (ws.loading) return
      setLoading(true)
      try {
        if (!fixtures) {
          await ws.refresh()
        }
        if (cancelled) return
        await loadActivity()
      } catch (err) {
        if (!cancelled) {
          setError(safeError(err, 'Unable to load recent activity. Please try again.'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // Refresh once when Live account id becomes available / workspace finishes loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally avoid ws.refresh identity churn
  }, [loadActivity, ws.loading, liveAccountId, fixtures])

  const showLiveHero = !ws.loading && !ws.hasLiveAccount

  return (
    <section className="cp-page cp-dash cp-dash--broker">
      {showLiveHero && liveAttemptFailed ? (
        <div className="cp-live-attempt-banner" role="status">
          <p>Previous attempt could not be completed.</p>
          <div className="cp-live-attempt-banner__actions">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setLiveAttemptFailed(false)
                setLiveModalOpen(true)
              }}
            >
              Retry
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setLiveAttemptFailed(false)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : null}

      {ws.loading ? (
        <section className="cp-live-status is-refreshing" aria-busy="true">
          <div className="cp-live-status__head">
            <h2>Live Trading Account</h2>
          </div>
        </section>
      ) : showLiveHero ? (
        <section className="cp-live-hero cp-live-hero--facts" aria-labelledby="live-hero-heading">
          <div className="cp-live-hero__copy">
            <span className="cp-badge cp-badge--live">Live Trading</span>
            <h2 id="live-hero-heading">Start Live Trading</h2>
            <p>Open your Live MetaTrader 5 account and fund it securely to begin trading.</p>
            <div className="cp-live-hero__actions">
              <Button type="button" variant="primary" onClick={() => setLiveModalOpen(true)}>
                Open Live Account
              </Button>
              <Button to={appPath('/app/accounts')} variant="secondary">
                Trading Accounts
              </Button>
            </div>
          </div>
          <dl className="cp-live-hero__facts" aria-label="Live account status">
            <div>
              <dt>Live Account</dt>
              <dd>Not Created</dd>
            </div>
            <div>
              <dt>Platform</dt>
              <dd>MetaTrader 5</dd>
            </div>
            <div>
              <dt>Opening Balance</dt>
              <dd className="cp-num">0 USD</dd>
            </div>
            <div>
              <dt>Trading Server</dt>
              <dd>iBull Capital - Live</dd>
            </div>
          </dl>
        </section>
      ) : liveAccount ? (
        <section className="cp-live-status" aria-labelledby="live-status-heading">
          <div className="cp-live-status__head">
            <span className={accountModeBadge(liveAccount.mode, liveAccount.status)}>
              {String(liveAccount.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
                ? 'Active'
                : String(liveAccount.status || '').toUpperCase().includes('PEND')
                  ? 'Pending'
                  : liveAccount.status}
            </span>
            <h2 id="live-status-heading">Live Trading Account</h2>
          </div>
          <dl className="cp-live-status__metrics">
            <div>
              <dt>MT5 Login</dt>
              <dd className="cp-num">{liveAccount.mt5Login}</dd>
            </div>
            <div>
              <dt>Trading Server</dt>
              <dd>iBull Capital - Live</dd>
            </div>
            <div>
              <dt>Balance</dt>
              <dd className="cp-num">
                {moneyAmount(liveBalance)} {liveAccount.currency || 'USD'}
              </dd>
            </div>
            <div>
              <dt>Equity</dt>
              <dd className="cp-num">
                {moneyAmount(liveEquity)} {liveAccount.currency || 'USD'}
              </dd>
            </div>
            <div>
              <dt>Available to Trade</dt>
              <dd className="cp-num">
                {moneyAmount(liveAvailable)} {liveAccount.currency || 'USD'}
              </dd>
            </div>
          </dl>
          <div className="cp-live-hero__actions">
            <Button to={appPath('/app/trade')} variant="primary">
              Trade Now
            </Button>
            <Button to={appPath('/app/deposits')} variant="secondary">
              Deposit Funds
            </Button>
            <Button to={appPath('/app/withdrawals')} variant="ghost">
              Withdraw Funds
            </Button>
            <Button to={appPath('/app/trading-history')} variant="ghost">
              Trading History
            </Button>
          </div>
        </section>
      ) : null}

      <QuickActions onOpenLive={() => setLiveModalOpen(true)} />

      {error ? (
        <div className="cp-banner cp-banner--error" role="alert">
          <p>{error}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void loadActivity()}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="cp-dash__activity">
        <section className={`cp-panel cp-panel--quiet${loading ? ' is-refreshing' : ''}`}>
          <div className="cp-panel__head">
            <h2>Recent Live Trades</h2>
            <Link to={appPath('/app/trading-history')}>View All</Link>
          </div>
          {loading ? (
            <div className="cp-empty cp-empty--compact" aria-busy="true">
              <p className="cp-muted">Loading recent trades…</p>
            </div>
          ) : !ws.hasLiveAccount ? (
            <div className="cp-empty cp-empty--compact">
              <p>
                <strong>No Live Trades Yet</strong>
              </p>
              <p className="cp-muted">Open a Live account to begin trading.</p>
            </div>
          ) : trades.length === 0 ? (
            <div className="cp-empty cp-empty--compact">
              <p>
                <strong>No Closed Trades Yet</strong>
              </p>
              <p className="cp-muted">Your completed Live trades will appear here.</p>
            </div>
          ) : (
            <div className="cp-card-list">
              {trades.map((t, idx) => {
                const pnl = Number(t.net_profit ?? t.profit ?? 0)
                const result = tradeResultLabel(t)
                const lots = t.volume != null ? Number(t.volume) : null
                const stake = lots != null && Number.isFinite(lots) ? lots * 10 : null
                return (
                  <article key={String(t.ticket ?? idx)} className="cp-row-card">
                    <div>
                      <strong>
                        {t.symbol || 'Trade'} · {t.side || '—'} · {result}
                      </strong>
                      <span className="cp-muted">
                        {stake != null ? `Stake $${stake.toFixed(2)}` : ''}
                        {lots != null && Number.isFinite(lots)
                          ? `${stake != null ? ' · ' : ''}${lots.toFixed(2)} lot`
                          : ''}
                        {t.close_time || t.time
                          ? ` · ${formatWhen(String(t.close_time || t.time))}`
                          : ''}
                      </span>
                    </div>
                    <div
                      className={`cp-num ${pnl < 0 ? 'is-neg' : pnl > 0 ? 'is-pos' : ''}`}
                    >
                      <span className="cp-muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                        Net P/L
                      </span>
                      {Number.isFinite(pnl) ? `${pnl > 0 ? '+' : ''}${pnl.toFixed(2)}` : '—'}
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </section>

        <section className={`cp-panel cp-panel--quiet${loading ? ' is-refreshing' : ''}`}>
          <div className="cp-panel__head">
            <h2>Recent Live Finance</h2>
            <Link to={appPath('/app/transactions')}>View All</Link>
          </div>
          {loading ? (
            <div className="cp-empty cp-empty--compact" aria-busy="true">
              <p className="cp-muted">Loading recent finance…</p>
            </div>
          ) : !ws.hasLiveAccount ? (
            <div className="cp-empty cp-empty--compact">
              <p>
                <strong>No Live Transactions Yet</strong>
              </p>
              <p className="cp-muted">Open a Live account, then fund it to begin.</p>
            </div>
          ) : txs.length === 0 ? (
            <div className="cp-empty cp-empty--compact">
              <p>
                <strong>No Transactions Yet</strong>
              </p>
              <p className="cp-muted">Your deposits and withdrawals will appear here.</p>
            </div>
          ) : (
            <div className="cp-card-list">
              {txs.map((tx, idx) => (
                <article key={String(tx.id ?? idx)} className="cp-row-card">
                  <div>
                    <strong>{financeTypeLabel(tx.type) || tx.reference || 'Transaction'}</strong>
                    <span className="cp-muted">
                      <span className={`cp-status cp-status--${financeStatusTone(tx.status)}`}>
                        {financeStatusLabel(tx.status)}
                      </span>
                      {tx.date || tx.created_at
                        ? ` · ${formatWhen(String(tx.date || tx.created_at))}`
                        : ''}
                    </span>
                  </div>
                  <div className="cp-num">
                    {moneyAmount(tx.amount)} {tx.currency || 'USD'}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <aside
        className={`cp-practice${ws.loading ? ' is-refreshing' : ''}`}
        aria-label="Practice account"
      >
          <div className="cp-practice__meta">
            <span className="cp-badge cp-badge--demo">Demo</span>
            <div>
              <strong>Practice Account</strong>
              <p className="cp-muted">
                Virtual Balance {demoAccount ? `$${moneyAmount(demoAccount.balance)}` : '$1,000.00'}
                {demoAccount ? ' · Active' : ''}
              </p>
            </div>
          </div>
          {demoAccount ? (
            <Button to={appPath('/app/accounts?mode=demo')} variant="ghost" size="sm">
              Practice Account Details
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={ws.creatingAccount}
              onClick={() => void ws.requestDemoAccount()}
            >
              {ws.creatingAccount ? 'Creating…' : 'Open Practice Account'}
            </Button>
          )}
        </aside>

      <LiveCreateModal
        open={liveModalOpen}
        onClose={() => setLiveModalOpen(false)}
        onCreate={async (p, c) => ws.requestLiveAccount(p, c)}
        onSucceeded={async () => {
          setLiveAttemptFailed(false)
          await ws.refresh()
          await loadActivity()
        }}
        onFailed={() => setLiveAttemptFailed(true)}
      />
    </section>
  )
}
